<script>
   /**************************************************************/
   /* SIMCASettings                                              */
   /* -------------------------------------                      */
   /* shows panel with SIMCA model settings.                     */
   /**************************************************************/

   import { qchisq } from 'mdatools/distributions';
   import { genhash } from '../../../shared/utils.js';

   import Select from '../../../shared/controls/Select.svelte';
   import Container from '../../../shared/controls/Container.svelte';
   import Range from '../../../shared/controls/Range.svelte';


   export let model;
   export let simca;
   export let ncomp;
   export let className = '';
   export let isAlt = false;

   /*****************************************/
   /* Component code                        */
   /*****************************************/


   // initialize simca object
   let alpha, gamma, limType;

   if (!simca) {
      alpha = 5;
      gamma = 1;
      limType = 'classic';

      const Nh = model.lim[limType].Nh;
      const h0 = model.lim[limType].h0;
      const Nq = model.lim[limType].Nq;
      const q0 = model.lim[limType].q0;

      const Ng = model.lim[limType].Ng;
      const g0 = model.lim[limType].g0;
      const Nr = model.lim[limType].Nr;
      const r0 = model.lim[limType].r0;

      const Nf = Nh.map((v, i) => v + Nq[i]);
      const Ni = Ng.map((v, i) => v + Nr[i]);

      const eCrit = Nf.map(v => qchisq(1 - alpha / 100, v));
      const oCrit = Nf.map(v => qchisq(Math.pow(1 - gamma / 100, 1 / model.nrows), v));
      const veCrit = Ni.map(v => qchisq(1 - alpha / 100, v));
      const voCrit = Ni.map(v => qchisq(Math.pow(1 - gamma / 100, 1 / model.ncols), v));

      simca = {
         ncomp: 1,
         nrows: model.nrows,
         ncols: model.ncols,
         alpha: alpha,
         gamma: gamma,
         limType,
         h0,
         q0,
         g0,
         r0,
         Nh,
         Nq,
         Nf,
         Ng,
         Nr,
         Ni,
         eCrit,
         oCrit,
         veCrit,
         voCrit,
         className,
         hash: genhash()
      };
   } else {
      alpha = simca.alpha;
      gamma = simca.gamma;
      limType = simca.limType;
      ncomp = simca.ncomp;
   }

   // initialize ncomp
   if (ncomp > model.ncomp) {
      ncomp = model.ncomp;
   }

   if (ncomp < 1) {
      ncomp = Number(simca.ncomp);
   }

   // possible choices for alpha and gamma
   const alphaOptions = [10, 5, 1, 0.1];
   const gammaOptions = [5, 1, 0.1, 0.01];


   /*****************************************/
   /* functions for changing model settings */
   /*****************************************/

   function setSimcaAlpha(alpha) {
      if (alpha === simca.alpha) return;
      simca.alpha =  alpha;
      simca.eCrit = simca.Nf.map(v => qchisq(1 - alpha / 100, v));
      simca.veCrit = simca.Ni.map(v => qchisq(1 - alpha / 100, v));
      simca.hash = genhash();
   }

   function setSimcaGamma(gamma) {
      if (gamma === simca.gamma) return;
      simca.gamma = gamma;
      simca.oCrit = simca.Nf.map(v => qchisq(Math.pow(1 - gamma / 100, 1 / simca.nrows), v));
      simca.voCrit = simca.Ni.map(v => qchisq(Math.pow(1 - gamma / 100, 1 / simca.ncols), v));
      simca.hash = genhash();
   }

   function setSimcaLimType(limType, model) {

      if (limType === simca.limType) return;
      simca.limType = limType;

      // object distance settings
      simca.Nh = model.lim[limType].Nh;
      simca.h0 = model.lim[limType].h0;
      simca.Nq = model.lim[limType].Nq;
      simca.q0 = model.lim[limType].q0;

      simca.Nf = simca.Nh.map((v, i) => v + simca.Nq[i]);
      simca.eCrit = simca.Nf.map(v => qchisq(1 - simca.alpha / 100, v));
      simca.oCrit = simca.Nf.map(v => qchisq(Math.pow(1 - simca.gamma / 100, 1 / simca.nrows), v));

      // variable distance settings
      simca.Ng = model.lim[limType].Ng;
      simca.g0 = model.lim[limType].g0;
      simca.Nr = model.lim[limType].Nr;
      simca.r0 = model.lim[limType].r0;

      simca.Ni = simca.Ng.map((v, i) => v + simca.Nr[i]);
      simca.veCrit = simca.Ni.map(v => qchisq(1 - simca.alpha / 100, v));
      simca.voCrit = simca.Ni.map(v => qchisq(Math.pow(1 - simca.gamma / 100, 1 / simca.ncols), v));
      simca.hash = genhash();
   }

   $: setSimcaAlpha(alpha, model)
   $: setSimcaGamma(gamma, model)
   $: setSimcaLimType(limType, model)
</script>

{#if simca}
<Container label="">
   <p class="title">Target class: {simca.className}</p>
</Container>

<Container label="# of PCs">
   <Range id="ncomp" bind:value={ncomp} min={1} max={model.ncomp} step={1} decNum={0} />
</Container>

<Container label='Alpha, %:'>
   <Select id='alpha' bind:value={alpha} options={alphaOptions} />
</Container>

{#if !isAlt}
<Container label='Gamma, %:'>
   <Select id='gamma' bind:value={gamma} options={gammaOptions} />
</Container>
{/if}

<Container label='Limits:'>
   <Select id='limType' bind:value={limType} options={['classic', 'robust']} />
</Container>
{/if}
